import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Divider } from '@mui/material';
import { TbIcon } from 'components/Figma/TbIcon';
import { cn } from 'utils/classNamesTailwind';
import type { MonthCell, MonthGridViewProps } from './MonthGridView.types';
import './MonthGridView.css';

const MONTH_ROWS_LEFT: MonthCell[][] = [
  [
    { number: 0, yearOffset: 0 },
    { number: 1, yearOffset: 0 },
    { number: 2, yearOffset: 0 },
    { number: 3, yearOffset: 0 },
  ],
  [
    { number: 4, yearOffset: 0 },
    { number: 5, yearOffset: 0 },
    { number: 6, yearOffset: 0 },
    { number: 7, yearOffset: 0 },
  ],
  [
    { number: 8, yearOffset: 0 },
    { number: 9, yearOffset: 0 },
    { number: 10, yearOffset: 0 },
    { number: 11, yearOffset: 0 },
  ],
];

const MONTH_ROWS_RIGHT: MonthCell[][] = [
  [
    { number: 0, yearOffset: 1 },
    { number: 1, yearOffset: 1 },
    { number: 2, yearOffset: 1 },
    { number: 3, yearOffset: 1 },
  ],
  [
    { number: 4, yearOffset: 1 },
    { number: 5, yearOffset: 1 },
    { number: 6, yearOffset: 1 },
    { number: 7, yearOffset: 1 },
  ],
  [
    { number: 8, yearOffset: 1 },
    { number: 9, yearOffset: 1 },
    { number: 10, yearOffset: 1 },
    { number: 11, yearOffset: 1 },
  ],
];

function isSameYearMonth(year: number, month: number, date: Date | undefined): boolean {
  if (!date) return false;
  return date.getFullYear() === year && date.getMonth() === month;
}

export function MonthGridView({
  selectedRange,
  minDate,
  maxDate,
  menuYear,
  onYearChange,
  onMonthClick,
  onMonthHover,
  rangePending,
}: MonthGridViewProps) {
  const intl = useIntl();

  const monthLabels = useMemo(
    () => [...Array(12).keys()].map(m => intl.formatDate(new Date(Date.UTC(2021, m)), { month: 'short' })),
    [intl]
  );

  const safeMinDate = minDate && maxDate && minDate > maxDate ? maxDate : minDate;

  const isDisabled = (m: MonthCell): boolean => {
    const cellYear = menuYear + m.yearOffset;
    const afterMax = maxDate
      ? cellYear > maxDate.getFullYear() || (cellYear === maxDate.getFullYear() && m.number > maxDate.getMonth())
      : false;
    const beforeMin = safeMinDate
      ? cellYear < safeMinDate.getFullYear() ||
        (cellYear === safeMinDate.getFullYear() && m.number < safeMinDate.getMonth())
      : false;
    return afterMax || beforeMin;
  };

  const isStart = (m: MonthCell): boolean => isSameYearMonth(menuYear + m.yearOffset, m.number, selectedRange?.from);

  const isEnd = (m: MonthCell): boolean => isSameYearMonth(menuYear + m.yearOffset, m.number, selectedRange?.to);

  const isInRange = (m: MonthCell): boolean => {
    if (!selectedRange?.from || !selectedRange?.to) return false;
    const cellYear = menuYear + m.yearOffset;
    const from = selectedRange.from;
    const to = selectedRange.to;
    const afterFrom = cellYear > from.getFullYear() || (cellYear === from.getFullYear() && m.number > from.getMonth());
    const beforeTo = cellYear < to.getFullYear() || (cellYear === to.getFullYear() && m.number < to.getMonth());
    return afterFrom && beforeTo;
  };

  const renderCell = (m: MonthCell) => {
    const disabled = isDisabled(m);
    const start = isStart(m);
    const end = isEnd(m);
    const inRange = isInRange(m);

    return (
      <td
        key={`${m.number}-${m.yearOffset}`}
        className={cn(
          'h-10 w-1/4 text-center text-sm p-0 relative',
          !disabled && !start && !end && 'hover:bg-snow-200',
          inRange && 'bg-snow-200',
          (start || end) && 'text-white month-grid-cell-selected'
        )}
        onMouseEnter={() => {
          if (!disabled && rangePending) onMonthHover(m, menuYear);
        }}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onMonthClick(m, menuYear)}
          className={cn(
            'h-full w-full p-0 font-normal',
            disabled && 'opacity-30 cursor-not-allowed',
            (start || end) && 'font-semibold'
          )}
        >
          {monthLabels[m.number]}
        </button>
      </td>
    );
  };

  const renderGrid = (rows: MonthCell[][]) => (
    <table className="w-full border-collapse">
      <tbody>
        {rows.map((row, rowIdx) => (
          <tr key={rowIdx} className="flex w-full">
            {row.map(renderCell)}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="min-w-[600px] pb-4 px-6 pt-6 flex gap-0">
      <div className="flex-1 flex flex-col gap-4">
        <div className="flex items-center justify-center relative h-7">
          <button
            type="button"
            onClick={() => onYearChange(menuYear - 1)}
            className="inline-flex items-center justify-center h-7 w-7 p-0 absolute left-0 opacity-50 hover:opacity-100"
          >
            <TbIcon name="ChevronLeft" size="lg" />
          </button>
          <span className="month-grid-year-label">{menuYear}</span>
        </div>
        {renderGrid(MONTH_ROWS_LEFT)}
      </div>

      <Divider orientation="vertical" flexItem className="month-grid-divider" />

      <div className="flex-1 flex flex-col gap-4">
        <div className="flex items-center justify-center relative h-7">
          <span className="month-grid-year-label">{menuYear + 1}</span>
          <button
            type="button"
            onClick={() => onYearChange(menuYear + 1)}
            className="inline-flex items-center justify-center h-7 w-7 p-0 absolute right-0 opacity-50 hover:opacity-100"
          >
            <TbIcon name="ChevronRight" size="lg" />
          </button>
        </div>
        {renderGrid(MONTH_ROWS_RIGHT)}
      </div>
    </div>
  );
}
